import { CustomSelector, ECustomSelector, Input } from '@globalmed/atlas-ui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './ProfileTab.module.css';
import { TCreateInstitutionForm } from '../../schema';
import { LocationFields } from '@/components/LocationFields';
import { translatedLanguageOptions } from '@/utils/languageOptions';

/**
 * Profile tab of the Create Institution modal — name, language, and address.
 * @returns ProfileTab component.
 */
export const ProfileTab = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<TCreateInstitutionForm>();

  const languageOptions = translatedLanguageOptions(t);

  return (
    <div className={styles.flexColumn}>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('admin-panel.institution-name')}
            placeholder={t('admin-panel.example-name')}
            width="100%"
            disabled={isSubmitting}
            {...field}
            isError={!!fieldState.error}
            message={t(fieldState.error?.message ?? '')}
            required
          />
        )}
      />
      <Controller
        name="language"
        control={control}
        render={({ field, fieldState }) => (
          <CustomSelector
            type={ECustomSelector.Select}
            labelProps={{
              label: t('admin-panel.institution-language'),
              required: true,
            }}
            options={languageOptions}
            selected={field.value}
            error={t(fieldState.error?.message ?? '')}
            onChange={field.onChange}
            sx={{ width: '100%' }}
          />
        )}
      />
      <div className={styles.row}>
        <LocationFields required stateLabel="common.state-territory" />
      </div>
      <div className={styles.row}>
        <Controller
          name="address1"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('common.address-line-number', { number: 1 })}
              placeholder={t('common.pocStreetPlaceholder')}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
              required
            />
          )}
        />
        <Controller
          name="address2"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('common.address-line-number', { number: 2 })}
              placeholder={t('common.addressAptLabel')}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
            />
          )}
        />
      </div>
      <div className={styles.row}>
        <Controller
          name="zip"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('common.zipCodeLabel')}
              placeholder={t('common.zipCodePlaceholder')}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
              required
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('common.city')}
              placeholder={t('common.city')}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
              required
            />
          )}
        />
      </div>
    </div>
  );
};
